import { getFactionStoredWrestlers } from './factionWrestlers';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const normalizeLineupSnapshot = (snapshot) => {
  if (!snapshot) return null;

  const champion = snapshot.champion || snapshot.championId || null;
  const inRing = [...(snapshot.inRing || snapshot.inRingIds || [])].filter(Boolean);
  const backstage = [...(snapshot.backstage || snapshot.backstageIds || [])].filter(Boolean);
  const hasAssignedWrestlers =
    Boolean(champion) || inRing.length > 0 || backstage.length > 0;

  if (!hasAssignedWrestlers) return null;

  return {
    effectiveFrom: toDate(snapshot.effectiveFrom),
    champion,
    inRing,
    backstage,
    mainEventerExpiresAt: toDate(snapshot.mainEventerExpiresAt),
  };
};

export const getMatchingFactionLineupSnapshot = (faction, referenceDate) => {
  const targetDate = toDate(referenceDate) || new Date();
  const history = Array.isArray(faction?.lineupHistory)
    ? faction.lineupHistory.map(normalizeLineupSnapshot).filter(Boolean)
    : [];
  const currentLineup = normalizeLineupSnapshot(faction?.currentLineup);
  const snapshots = [...history];

  if (currentLineup) {
    const alreadyInHistory = snapshots.some(
      (snapshot) =>
        snapshot.effectiveFrom &&
        currentLineup.effectiveFrom &&
        snapshot.effectiveFrom.getTime() === currentLineup.effectiveFrom.getTime()
    );

    if (!alreadyInHistory) {
      snapshots.push(currentLineup);
    }
  }

  return snapshots
    .filter((snapshot) => !snapshot.effectiveFrom || snapshot.effectiveFrom <= targetDate)
    .sort(
      (a, b) =>
        (b.effectiveFrom?.getTime() ?? Number.NEGATIVE_INFINITY) -
        (a.effectiveFrom?.getTime() ?? Number.NEGATIVE_INFINITY)
    )[0] || null;
};

export const resolveLineupRoleFromSnapshot = (snapshot, wrestlerId, referenceDate) => {
  if (!snapshot || !wrestlerId) return null;

  const targetDate = toDate(referenceDate) || new Date();
  const mainEventerActive =
    snapshot.champion === wrestlerId &&
    (!snapshot.mainEventerExpiresAt || snapshot.mainEventerExpiresAt >= targetDate);

  if (mainEventerActive) return 'Main_Eventer';
  if (snapshot.backstage.includes(wrestlerId)) return 'Backstage';
  if (snapshot.inRing.includes(wrestlerId)) return 'In_Ring';
  return null;
};

export const getFactionMemberRoleAtDateFromLineup = (faction, wrestlerId, referenceDate) => {
  const matchingSnapshot = getMatchingFactionLineupSnapshot(faction, referenceDate);

  if (matchingSnapshot) {
    return resolveLineupRoleFromSnapshot(matchingSnapshot, wrestlerId, referenceDate);
  }

  const storedMember = getFactionStoredWrestlers(faction).find(
    (member) => member.id === wrestlerId
  );

  return storedMember?.status || null;
};

export const getEffectiveFactionMemberStatus = ({
  faction,
  wrestlerId,
  fallbackStatus = null,
  referenceDate = new Date(),
}) => {
  if (!wrestlerId) return fallbackStatus;

  const matchingSnapshot = getMatchingFactionLineupSnapshot(faction, referenceDate);
  if (!matchingSnapshot) return fallbackStatus;

  return resolveLineupRoleFromSnapshot(matchingSnapshot, wrestlerId, referenceDate);
};
